import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { List, useTable, CreateButton } from '@refinedev/antd';
import { useInvalidate } from '@refinedev/core';
import { Tag } from 'antd';
import { UserProfile } from '../../api/types';
import { deactivateUser, reactivateUser } from '../../api/userDeactivation';
import { fetchAllForExport } from '../../api/leadsExport';
import {
  DataTable,
  DeactivateUserModal,
  RowActionsMenu,
  roleLabel,
  useUrlTableState,
  type DataColumn,
  type TableState,
} from '@viq-admin/ui';

/**
 * Portal counterpart to the console's cross-tenant user list
 * (apps/console/src/resources/users/list.tsx) — same DataTable column
 * template (name / email / role / last login / status), minus console's
 * `companyName` column: the portal is single-tenant (every row is the
 * caller's own company), so there is nothing to show there.
 *
 * Opening a user is the ROW CLICK (→ `/users/:oid`, `UserDetailView`) — see
 * the DataTable spec. Unlike console, the portal has NO user-edit page, so
 * there is no Edit kebab entry here (console's is a documented, B2-pending
 * exception that does not apply to the portal).
 *
 * The pre-conversion list had a dedicated `<Segmented>` Active/Inactive
 * control above the table, defaulting to Active-only. That capability now
 * lives in the Status column's standard `kind: 'enum'` filter — identical in
 * shape to console's own Status column — rather than a portal-only fork of
 * the shared table. `DEFAULT_STATE.filters` seeds that column filter to
 * `['Active']` so the Active-only default survives the conversion (a bare
 * `sort`-only default would silently show deactivated users on load); the
 * filter itself remains fully interactive — Inactive, or both, is one click
 * away in the Status column header, same UI as console's Status column.
 *
 * `read:User` gates the whole page via the route's `RoleGuard`
 * (company_admin only, apps/portal/src/App.tsx); `manage:User` gates the
 * per-row Deactivate/Reactivate actions — same effective reachability as
 * before (`ADMIN_ACTION_ROLES = ['company_admin']` in the pre-conversion
 * file), now also enforced at the component level via `RowActionsMenu`.
 */

const ROLE_COLOR: Record<string, string> = {
  field_rep: 'green',
  company_admin: 'blue',
  stakeholder: 'gold',
};

/**
 * Seeds the default sort when the URL carries none. Module-level so its
 * identity is stable across renders — see DataTable's `defaultState` JSDoc.
 */
const DEFAULT_STATE: Partial<TableState> = {
  sort: { field: 'name', order: 'ascend' },
  // Preserves the pre-conversion Active-only default (the old dedicated
  // <Segmented> defaulted to Active, hiding deactivated users on load).
  // 'status' is the Status column's stateKeyOf(col.key) — see DataTable's
  // `stateKeyOf` — and 'Active' is the exact string its `projectValue`
  // projects a non-deactivated row to. Fully operable: switching the Status
  // filter to Inactive (or selecting both) still works via the normal
  // column-filter UI, same as console's un-narrowed Status column.
  filters: { status: ['Active'] },
};

interface DeactivateTarget {
  oid: string;
  name: string;
}

export function UserList() {
  const { tableProps } = useTable<UserProfile>({
    resource: 'users',
    // DataTable owns paging; Refine must not slice the payload.
    pagination: { mode: 'off' },
  });
  const invalidate = useInvalidate();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useUrlTableState(searchParams, setSearchParams);
  const [deactivateTarget, setDeactivateTarget] =
    useState<DeactivateTarget | null>(null);

  const columns: DataColumn<UserProfile>[] = [
    { dataIndex: 'name', key: 'name', title: 'Name', kind: 'text', width: 200 },
    { dataIndex: 'email', key: 'email', title: 'Email', kind: 'text', width: 260 },
    {
      dataIndex: 'role',
      key: 'role',
      title: 'Role',
      kind: 'enum',
      width: 140,
      render: (v: string) => <Tag color={ROLE_COLOR[v] ?? 'default'}>{roleLabel(v)}</Tag>,
      projectValue: (r) => roleLabel(r.role),
    },
    {
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      title: 'Last login',
      kind: 'dateTime',
      width: 200,
    },
    {
      dataIndex: 'deactivatedAt',
      key: 'status',
      title: 'Status',
      kind: 'enum',
      width: 100,
      render: (v: string | null) =>
        v ? <Tag>Inactive</Tag> : <Tag color="green">Active</Tag>,
      projectValue: (r) => (r.deactivatedAt ? 'Inactive' : 'Active'),
    },
    {
      key: 'actions',
      title: '',
      kind: 'actions',
      render: (_: unknown, record: UserProfile) => {
        const inactive = Boolean(record.deactivatedAt);
        return (
          <RowActionsMenu
            actions={[
              inactive
                ? {
                    key: 'reactivate',
                    label: 'Reactivate user',
                    cap: 'manage:User',
                    onRun: async () => {
                      await reactivateUser(record.oid);
                      invalidate({ resource: 'users', invalidates: ['list'] });
                    },
                    confirm: {
                      title: `Reactivate ${record.name}?`,
                      content: `${record.name} will regain access immediately.`,
                      okText: 'Reactivate',
                      successMessage: `${record.name} reactivated.`,
                    },
                  }
                : {
                    key: 'deactivate',
                    label: 'Deactivate user',
                    danger: true,
                    cap: 'manage:User',
                    // Opens the shared DeactivateUserModal below rather than
                    // RowActionsMenu's built-in confirm dialog: the modal
                    // collects an optional reason and shows the caller's
                    // attributed-capture count before submitting.
                    onRun: () =>
                      setDeactivateTarget({ oid: record.oid, name: record.name }),
                  },
            ]}
          />
        );
      },
    },
  ];

  return (
    <>
      <List headerButtons={[<CreateButton key="create" />]}>
        <DataTable<UserProfile>
          tableProps={tableProps}
          columns={columns}
          rowKey="oid"
          storageKey="usersList"
          state={state}
          onStateChange={setState}
          defaultState={DEFAULT_STATE}
          searchFields={['name', 'email']}
          searchPlaceholder="Search users"
          onRowClick={(record) => navigate(`/users/${record.oid}`)}
          empty={{
            title: 'No users yet',
            description: 'Invite the first user to get started.',
          }}
        />
      </List>

      {deactivateTarget && (
        <DeactivateUserModal
          oid={deactivateTarget.oid}
          displayName={deactivateTarget.name}
          open={true}
          onClose={() => setDeactivateTarget(null)}
          onSuccess={() =>
            invalidate({ resource: 'users', invalidates: ['list'] })
          }
          onDeactivate={(oid, reason) => deactivateUser(oid, reason)}
          fetchCaptureCount={(oid) =>
            fetchAllForExport({ repOid: oid }).then((r) => r.rows.length)
          }
        />
      )}
    </>
  );
}
